import { createApi } from "@reduxjs/toolkit/query/react";
import { setMachine } from "./machinesSlice";
import baseQueryWithReauth from "../../common/utils/baseQueryReauth";

import config from "../../config";
import { isEmpty } from "lodash";
const { envs } = config;
const { machines: apiPath } = config.endpoints;

const resetLayoutOnSuccess = async (arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
    dispatch(setMachine({}));
  } catch (error) { }
}

const machinesService = createApi({
 reducerPath: "machinesService",
 baseQuery: baseQueryWithReauth,
 tagTypes: ["Machines"],
 endpoints: (builder) => ({
  getMachinesCountry: builder.query({
   // note: an optional `queryFn` may be used in place of `query`
   query: (country) => ({ url: apiPath.getMachinesCountry(country) }),
   // Pick out data and prevent nested properties in a hook or selector
   transformResponse: (response, meta, arg) => response,
   // Pick out errors and prevent nested properties in a hook or selector
   transformErrorResponse: (response, meta, arg) => response.status,
   providesTags: (result, error, id) => [{ type: "Machines", id }],
   // The 2nd parameter is the destructured `QueryLifecycleApi`
   async onQueryStarted(
    arg,
    {
     dispatch,
     getState,
     extra,
     requestId,
     queryFulfilled,
     getCacheEntry,
     updateCachedData,
    }
   ) {},
   // The 2nd parameter is the destructured `QueryCacheLifecycleApi`
   async onCacheEntryAdded(
    arg,
    {
     dispatch,
     getState,
     extra,
     requestId,
     cacheEntryRemoved,
     cacheDataLoaded,
     getCacheEntry,
     updateCachedData,
    }
   ) {},
  }),
  getMachinesModel: builder.query({
   // note: an optional `queryFn` may be used in place of `query`
   query: (modelNumber) => ({ url: apiPath.getMachinesModel(modelNumber) }),
   // Pick out data and prevent nested properties in a hook or selector
   transformResponse: (response, meta, arg) => response,
   // Pick out errors and prevent nested properties in a hook or selector
   transformErrorResponse: (response, meta, arg) => response.status,
   providesTags: (result, error, id) => [{ type: "Machines", id }],
   // The 2nd parameter is the destructured `QueryLifecycleApi`
   async onQueryStarted(
    arg,
    {
     dispatch,
     getState,
     extra,
     requestId,
     queryFulfilled,
     getCacheEntry,
     updateCachedData,
    }
   ) {},
   // The 2nd parameter is the destructured `QueryCacheLifecycleApi`
   async onCacheEntryAdded(
    arg,
    {
     dispatch,
     getState,
     extra,
     requestId,
     cacheEntryRemoved,
     cacheDataLoaded,
     getCacheEntry,
     updateCachedData,
    }
   ) {},
  }),
  getMachines: builder.query({
   // note: an optional `queryFn` may be used in place of `query`
   query: () => ({ url: apiPath.getMachines }),
   // Pick out data and prevent nested properties in a hook or selector
   transformResponse: (response, meta, arg) => {
    if (isEmpty(response)) {
      return null;
    }

    return response.map((machine) => ({
     ...machine,
     content: JSON.parse(machine.content),
    }));
   },
   // Pick out errors and prevent nested properties in a hook or selector
   transformErrorResponse: (response, meta, arg) => response.status,
   providesTags: (result) =>
    // is result available?
    result
     ? // successful query
       [
        ...result.map(({ model }) => ({ type: "Machines", id: model })),
        { type: "Machines", id: "LIST" },
       ]
     : // an error occurred, but we still want to refetch this query when `{ type: 'Machines', id: 'LIST' }` is invalidated
       [{ type: "Machines", id: "LIST" }],
  }),
  postMachine: builder.mutation({
   query: ({id,  ...machine}) => ({
    url: apiPath.postMachine,
    method: "POST",
    body: {
     ...machine,
     content: JSON.stringify(machine.content),
    },
   }),
   // Pick out data and prevent nested properties in a hook or selector
   transformResponse: (response, meta, arg) => response.data,
   // Pick out errors and prevent nested properties in a hook or selector
   transformErrorResponse: (response, meta, arg) => response.status,
   // Invalidates all Post-type queries providing the `LIST` id - after all, depending of the sort order,
   // that newly created post could show up in any lists.
   invalidatesTags: ["Machines"],
   // onQueryStarted is useful for optimistic updates
   // The 2nd parameter is the destructured `MutationLifecycleApi`
   onQueryStarted: resetLayoutOnSuccess,
   // The 2nd parameter is the destructured `MutationCacheLifecycleApi`
   async onCacheEntryAdded(
    arg,
    {
     dispatch,
     getState,
     extra,
     requestId,
     cacheEntryRemoved,
     cacheDataLoaded,
     getCacheEntry,
    }
   ) {},
  }),
  putMachine: builder.mutation({
   query: (machine) => ({
    // url: apiPath.putMachine(machine.dsn),
    url: apiPath.postMachine,
    method: "POST",
    body: {
     ...machine,
     content: JSON.stringify(machine.content),
    },
   }),
   onQueryStarted: resetLayoutOnSuccess,
   // Invalidates all queries that subscribe to this Machine `id` only.
   // In this case, `getMachine` will be re-run. `getMachines` *might*  rerun, if this id was under its results.
   invalidatesTags: ["Machines"],
   //  invalidatesTags: (result, error, { dsn }) => [
   //   { type: "Machines", id: dsn },
   //  ],
  }),
  deleteMachine: builder.mutation({
   query: (machine) => ({
    url: apiPath.deleteMachine(machine.dsn),
    method: "DELETE",
   }),
   onQueryStarted: resetLayoutOnSuccess,
   // Invalidates all queries that subscribe to this Machine `id` only.
   invalidatesTags: ["Machines"],
  }),
 }),
});

export default machinesService;

export const {
  useGetMachinesQuery,
  useGetMachinesCountryQuery,
  useGetMachinesModelQuery,
  usePostMachineMutation,
  usePutMachineMutation,
  useDeleteMachineMutation
} = machinesService;
